"use client";

import { useTransition, useState, MouseEvent } from "react";
import { Heart } from "lucide-react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { useFavorites } from "@/contexts/favoriteContext";

interface FavoriteButtonProps {
  cardId: string;
  initialIsFavorite?: boolean;
  className?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  onToggle?: (isFavorite: boolean) => void;
}

export default function FavoriteButton({
  cardId,
  initialIsFavorite = false,
  className = "",
  onClick,
  onToggle,
}: FavoriteButtonProps) {
  const [isPending, startTransition] = useTransition();
  const [isAnimating, setIsAnimating] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();
  const { isFavorite: checkFavorite, toggleFavorite } = useFavorites();

  const isCardFavorite = checkFavorite(cardId) || initialIsFavorite;

  const triggerAnimation = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
    }, 700);
  };

  const handleToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (onClick) {
      triggerAnimation();
      onClick(e);
      return;
    }

    // Not logged in: keep the user on the page and let them opt in to logging in
    if (!session) {
      toast("Please log in to save cards to your favorites.", {
        id: "favorite-login-required",
        action: {
          label: "Log in",
          onClick: () => {
            if (typeof window !== "undefined") {
              sessionStorage.setItem("redirectAfterLogin", window.location.pathname);
            }
            router.push("/auth/login");
          },
        },
      });
      return;
    }

    triggerAnimation();

    startTransition(async () => {
      await toggleFavorite(cardId);
      if (onToggle) {
        onToggle(!isCardFavorite);
      }
    });
  };

  return (
    <div className={`absolute top-2 left-2 z-20 inline-flex items-center justify-center ${className}`}>
      {/* Expanding Ripple Ring Animation on Like */}
      {isAnimating && (
        <span
          className="absolute inset-0 rounded-full border-2 border-rose-500 pointer-events-none"
          style={{
            animation: "heartRing 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) forwards",
          }}
        />
      )}

      {/* Sparkle Confetti Particles on Like */}
      {isAnimating && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          {[
            { tx: "14px", ty: "0px", bg: "bg-rose-500" },
            { tx: "7px", ty: "-13px", bg: "bg-amber-400" },
            { tx: "-7px", ty: "-13px", bg: "bg-pink-400" },
            { tx: "-14px", ty: "0px", bg: "bg-rose-500" },
            { tx: "-7px", ty: "13px", bg: "bg-amber-400" },
            { tx: "7px", ty: "13px", bg: "bg-rose-400" },
          ].map((particle, idx) => (
            <span
              key={idx}
              className={`absolute w-1.5 h-1.5 rounded-full ${particle.bg}`}
              style={
                {
                  "--tx": particle.tx,
                  "--ty": particle.ty,
                  animation: `particleBurst 0.65s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={handleToggle}
        disabled={isPending}
        aria-label={isCardFavorite ? "Remove from favorites" : "Add to favorites"}
        title={isCardFavorite ? "Remove from favorites" : "Add to favorites"}
        className={`relative z-10 p-1.5 rounded-full backdrop-blur-md transition-all duration-300 shadow-sm cursor-pointer select-none group/fav ${
          isCardFavorite
            ? "bg-white/95 text-rose-500 hover:bg-white shadow-rose-500/20 shadow-md ring-1 ring-rose-100"
            : "bg-black/35 text-white hover:bg-black/55 hover:text-rose-300"
        } hover:scale-115 active:scale-85 ${
          isAnimating && isCardFavorite ? "scale-110" : ""
        }`}
      >
        <Heart
          className={`w-4 h-4 transition-all duration-200 ${
            isCardFavorite
              ? "fill-rose-500 stroke-rose-500 drop-shadow-[0_2px_4px_rgba(244,63,94,0.35)]"
              : "stroke-current fill-none group-hover/fav:scale-110"
          } ${
            isAnimating && isCardFavorite ? "animate-heart-pop" : ""
          }`}
          style={
            isAnimating && isCardFavorite
              ? {
                  animation:
                    "heartPop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards",
                }
              : undefined
          }
        />
      </button>

      {/* Embedded keyframe styles for smooth hardware-accelerated animations */}
      <style jsx global>{`
        @keyframes heartPop {
          0% {
            transform: scale(1);
          }
          25% {
            transform: scale(0.6);
          }
          50% {
            transform: scale(1.45);
          }
          75% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes heartRing {
          0% {
            transform: scale(0.5);
            opacity: 0.9;
            border-color: #f43f5e;
          }
          50% {
            opacity: 0.6;
          }
          100% {
            transform: scale(2.3);
            opacity: 0;
            border-color: #fb7185;
          }
        }

        @keyframes particleBurst {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 1;
          }
          40% {
            opacity: 1;
          }
          100% {
            transform: translate(var(--tx), var(--ty)) scale(0);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
